import {ObjectModelTriggerType} from "../../enums/objectModelTriggerType";
import assertNever from "../../../global/assetNever";
import {ObjectModelActionType} from "~/enums/objectModelActionType";

export default class ObjectModelTriggerDefinition {
  static getComponent(triggerType: ObjectModelTriggerType) {
    switch (triggerType) {
      case ObjectModelTriggerType.DefaultTrigger:
        return 'default-trigger';
      case ObjectModelTriggerType.AutoGenerateForm:
        return 'auto-generate-form';
      case ObjectModelTriggerType.AutomaticNotification:
        return 'automatic-notification';
      case ObjectModelTriggerType.UpdatePropertyValues:
        return 'update-property-values';
      case ObjectModelTriggerType.RunMapping:
        return 'run-mapping';
      case ObjectModelTriggerType.LogEventInHistory:
        return 'log-event-in-history';
      case ObjectModelTriggerType.RemoveRelationship:
        return 'remove-relationship';
      case ObjectModelTriggerType.EstablishRelationship:
        return 'establish-relationship';
      case ObjectModelTriggerType.RunProcess:
        return 'run-process';
      default:
        assertNever(triggerType);
    }
  }

  static getDisplayName(triggerType: ObjectModelTriggerType) {
    switch (triggerType) {
      case ObjectModelTriggerType.DefaultTrigger:
        return 'Default Trigger';
      case ObjectModelTriggerType.AutoGenerateForm:
        return 'Auto-generation of a form';
      case ObjectModelTriggerType.AutomaticNotification:
        return 'Automatic Notifications';
      case ObjectModelTriggerType.UpdatePropertyValues:
        return 'Update Property Values';
      case ObjectModelTriggerType.RunMapping:
        return 'Run a Mapping';
      case ObjectModelTriggerType.LogEventInHistory:
        return 'Log Event in History';
      case ObjectModelTriggerType.RemoveRelationship:
        return 'Remove Relationship(s)';
      case ObjectModelTriggerType.EstablishRelationship:
        return 'Establish Relationship(s)';
      case ObjectModelTriggerType.RunProcess:
        return 'Run a Process';
      default:
        assertNever(triggerType);
    }
  }

  static getIcon(triggerType: ObjectModelTriggerType) {
    switch (triggerType) {
      case ObjectModelTriggerType.DefaultTrigger:
        return 'trigger';
      case ObjectModelTriggerType.AutoGenerateForm:
        return 'form';
      case ObjectModelTriggerType.UpdatePropertyValues:
        return 'dynamic-field';
      case ObjectModelTriggerType.AutomaticNotification:
        return 'email';
      case ObjectModelTriggerType.RunMapping:
        return 'run-process';
      case ObjectModelTriggerType.LogEventInHistory:
        return 'clock';
      case ObjectModelTriggerType.RemoveRelationship:
        return 'trash';
      case ObjectModelTriggerType.EstablishRelationship:
        return 'relationships';
      case ObjectModelTriggerType.RunProcess:
        return 'process-flow'
      default:
        assertNever(triggerType);
    }
  }

  static getIconColor(triggerType: ObjectModelTriggerType) {
    switch (triggerType) {
      case ObjectModelTriggerType.DefaultTrigger:
        return 'green';
      case ObjectModelTriggerType.AutoGenerateForm:
        return 'blue';
      case ObjectModelTriggerType.UpdatePropertyValues:
        return 'orange';
      case ObjectModelTriggerType.AutomaticNotification:
        return 'green';
      case ObjectModelTriggerType.RunMapping:
        return 'dark-purple';
      case ObjectModelTriggerType.LogEventInHistory:
        return 'lake';
      case ObjectModelTriggerType.RemoveRelationship:
        return 'fire-brick';
      case ObjectModelTriggerType.EstablishRelationship:
        return 'fuchsia';
      case ObjectModelTriggerType.RunProcess:
        return 'blue';
      default:
        assertNever(triggerType);
    }
  }

  static getComponentClass(triggerType: ObjectModelTriggerType) {
    return `builder-element element-${ObjectModelTriggerDefinition.getComponent(triggerType)} omt-${ObjectModelTriggerDefinition.getComponent(triggerType)}`;
  }

  static getControlGroups(): { name: string, icon: string, iconColor:string, type: ObjectModelTriggerType }[] {
    return [
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.AutoGenerateForm),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.AutoGenerateForm),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.AutoGenerateForm),
        type: ObjectModelTriggerType.AutoGenerateForm
      },
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.AutomaticNotification),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.AutomaticNotification),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.AutomaticNotification),
        type: ObjectModelTriggerType.AutomaticNotification
      },
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.RunProcess),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.RunProcess),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.RunProcess),
        type: ObjectModelTriggerType.RunProcess
      },
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.RunMapping),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.RunMapping),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.RunMapping),
        type: ObjectModelTriggerType.RunMapping
      },
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.LogEventInHistory),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.LogEventInHistory),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.LogEventInHistory),
        type: ObjectModelTriggerType.LogEventInHistory
      },
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.UpdatePropertyValues),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.UpdatePropertyValues),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.UpdatePropertyValues),
        type: ObjectModelTriggerType.UpdatePropertyValues
      },
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.EstablishRelationship),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.EstablishRelationship),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.EstablishRelationship),
        type: ObjectModelTriggerType.EstablishRelationship
      },
      {
        name: ObjectModelTriggerDefinition.getDisplayName(ObjectModelTriggerType.RemoveRelationship),
        icon: ObjectModelTriggerDefinition.getIcon(ObjectModelTriggerType.RemoveRelationship),
        iconColor: ObjectModelTriggerDefinition.getIconColor(ObjectModelTriggerType.RemoveRelationship),
        type: ObjectModelTriggerType.RemoveRelationship
      },
    ];
  }
}